import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, IconButton, Tooltip } from '@mui/material'
import { NewTable } from 'components/datatable/NewTable'
import i18n from 'simple-react-i18n'
import { useDispatch } from 'react-redux'
import { isEqual, isNil, keyBy, orderBy, round } from 'lodash'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import IndicatorDialogLayout from './IndicatorDialogLayout'
import { InputTable } from './IndicatorTable'
import RpqsAction from 'rpqs/actions/RpqsAction'
import { resolveFormula } from 'rpqs/utils/RPQSFormula'
import { formatIndicatorValue, getStatusFromCode } from 'rpqs/utils/RPQSUtils'
import { rpqsIndicatorShape } from 'rpqs/constants/RpqsShapes'
import Select from '../../../../components/forms/Select'
import { FIELD_TYPE_BOOL, FIELD_TYPE_FLOAT } from '../../../constants/RpqsConstant'

const formatComputed = (result, indicator) => (indicator.fieldType === FIELD_TYPE_FLOAT ? round(result, 2) : result)

const booleanOptions = () => [{ code: 'true', name: i18n.yes }, { code: 'false', name: i18n.no }]

const fixedColumnsSx = headers => ({
    '& table': { tableLayout: 'fixed' },
    '& td': { borderBottom: 'none' },
    ...Object.fromEntries(headers.map((header, i) => [`& td:nth-of-type(${i + 1})`, { width: header.width }])),
})

const RpqsIndicatorDialog = ({
    open = false,
    setOpen = () => {},
    mainCode = '',
    indicators = [],
    rpqs = {},
    egId,
    refValues = {},
    onSaved = () => {},
}) => {
    const dispatch = useDispatch()
    const [values, setValues] = useState({})
    const [initialValues, setInitialValues] = useState({})
    const [notes, setNotes] = useState('')
    const [freeInput, setFreeInput] = useState(false)
    const [localRefValues, setLocalRefValues] = useState({})

    const year = rpqs.year

    const group = useMemo(
        () => orderBy(indicators.filter(ind => ind.code === mainCode || ind.parent === mainCode), ind => ind.order ?? 0),
        [indicators, mainCode],
    )
    const main = group.find(ind => ind.code === mainCode)
    const backValueByCode = useMemo(() => keyBy(indicators, 'code'), [indicators])

    const allRefValues = { ...refValues, ...localRefValues }

    const needsRef002 = group.some(ind => `${ind.formula || ''} ${ind.proposedFormula || ''}`.includes('REF.002'))
    useEffect(() => {
        if (!open || !needsRef002) return
        const years = [rpqs.year - 1, rpqs.year - 2, rpqs.year - 3]
        dispatch(RpqsAction.fetchIndicatorValues({
            collectivityId: rpqs.collectivityId,
            years,
            managementUnitId: parseInt(egId, 10),
            codes: ['P104.3'],
        })).then(res => {
            if (!res) return
            const valueByYear = keyBy(res, 'year')
            const previousValues = years.map(y => parseFloat(valueByYear[y]?.value))
            // Les 3 années doivent avoir une valeur, sinon on ne calcule pas.
            const average = previousValues.some(Number.isNaN) ? undefined : round(previousValues.reduce((sum, v) => sum + v, 0) / years.length, 2)
            setLocalRefValues(prev => ({ ...prev, 'REF.002': average }))
        })
    }, [open, mainCode])

    const valueResolver = source => code => {
        const local = source[code]
        return !isNil(local) && local !== '' ? local : backValueByCode[code]?.value
    }

    const recompute = (currentValues, free) => group.reduce((acc, ind) => {
        if (!ind.formula || (free && !ind.readOnly)) return acc
        const result = resolveFormula(ind.formula, { getValue: valueResolver(acc), refValues: allRefValues })
        return { ...acc, [ind.code]: isNil(result) ? '' : `${formatComputed(result, ind)}` }
    }, currentValues)

    const isComputedEditable = ind => !!ind.formula && !ind.readOnly

    useEffect(() => {
        if (!open) return
        const initial = Object.fromEntries(group.map(ind => [ind.code, ind.value ?? '']))
        const savedFreeInput = group.some(ind => isComputedEditable(ind) && ind.manualInput)
        const initialComputed = recompute(initial, savedFreeInput)
        setValues(initialComputed)
        setInitialValues(initialComputed)
        setNotes(main?.notes ?? '')
        setFreeInput(savedFreeInput)
    }, [open, mainCode])

    const handleChangeValue = (code, value) => {
        setValues(prev => recompute({ ...prev, [code]: value }, freeInput))
    }

    const handleToggleFreeInput = (free) => {
        setFreeInput(free)
        if (!free) setValues(prev => recompute(prev, false))
    }

    const isEditable = ind => !ind.readOnly && (!ind.formula || freeInput)

    const valueField = ind => ind.fieldType === FIELD_TYPE_BOOL ? (
        <Select
            value={values[ind.code] ?? ''}
            options={booleanOptions()}
            onChange={v => handleChangeValue(ind.code, v ?? '')}
            readMode={!isEditable(ind)}
        />
    ) : (
        <InputTable
            value={values[ind.code] ?? ''}
            onChange={v => handleChangeValue(ind.code, v)}
            readMode={!isEditable(ind)}
            fieldType={ind.fieldType}
        />
    )

    const proposedValueOf = ind => {
        if (!ind.proposedFormula) return undefined
        const result = resolveFormula(ind.proposedFormula, { getValue: valueResolver(values), refValues: allRefValues })
        return isNil(result) ? undefined : formatComputed(result, ind)
    }

    const handleRegister = () => {
        const body = {
            year: rpqs.year,
            collectivityId: rpqs.collectivityId,
            managementUnitId: parseInt(egId, 10),
            mainCode,
            notes,
            indicators: group.map(ind => ({
                code: ind.code,
                value: values[ind.code] ?? '',
                // Un indicateur partagé non modifiable dans cette popin garde son état de saisie manuelle.
                manualInput: isComputedEditable(ind) ? freeInput : !!ind.manualInput,
            })),
        }
        dispatch(RpqsAction.updateIndicators(body)).then(json => {
            if (json?.update) {
                onSaved()
                setOpen(false)
            }
        })
    }

    const titleActions = [
        { icon: 'edit', tooltip: i18n.switchInputMode, onClick: () => handleToggleFreeInput(true), displayed: !freeInput },
        { icon: 'edit_off', tooltip: i18n.switchToAuto, onClick: () => handleToggleFreeInput(false), displayed: freeInput },
        { icon: 'close', tooltip: i18n.close, onClick: () => setOpen(false) },
    ]

    const headers = [
        { key: 'indicator', value: i18n.indicator, width: '10%' },
        { key: 'name', value: i18n.indicatorDenomination, width: '35%' },
        { key: 'previousValue', value: `${year - 1}`, width: '12%' },
        { key: 'value', value: `${year}`, width: '13%' },
        { key: 'unit', value: i18n.unit, width: '15%' },
        { key: 'proposedValue', value: i18n.proposedValue, width: '15%' },
    ]

    const rowOf = ind => {
        const proposed = proposedValueOf(ind)
        return {
            // INT.* = valeurs internes : on affiche l'indicateur mais sans son code.
            indicator: ind.code.startsWith('INT.') ? '' : ind.code,
            name: (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textWrap: 'wrap' }}>
                    {ind.name}
                    {ind.comment &&
                        <Tooltip title={ind.comment}>
                            <InfoOutlinedIcon fontSize='small' color='action'/>
                        </Tooltip>
                    }
                </Box>
            ),
            previousValue: formatIndicatorValue(ind.previousValue ?? '', ind.fieldType),
            value: valueField(ind),
            unit: ind.unit,
            proposedValue: isNil(proposed) ? '' : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{formatIndicatorValue(proposed, ind.fieldType)}</span>
                    <Tooltip title={i18n.useThisValue}>
                        <IconButton size='small' onClick={() => handleChangeValue(ind.code, `${proposed}`)}>
                            <ContentCopyIcon fontSize='small'/>
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        }
    }

    const columnsSx = fixedColumnsSx(headers)

    return (
        <IndicatorDialogLayout
            open={open}
            title={main ? `${main.code} - ${main.mainName || main.name}` : ''}
            titleActions={titleActions}
            notes={notes}
            onChangeNotes={setNotes}
            status={getStatusFromCode(main?.status)}
            onRegister={handleRegister}
            hasChanges={!isEqual(values, initialValues)}
            footer={main && (
                <Box sx={columnsSx}>
                    <NewTable headers={headers} rows={[rowOf(main)]} rowsPerPageOptions={[1000]} displayHeaders={false}/>
                </Box>
            )}
        >
            <Box sx={columnsSx}>
                <NewTable headers={headers} rows={group.filter(ind => ind.code !== mainCode).map(rowOf)} rowsPerPageOptions={[1000]}/>
            </Box>
        </IndicatorDialogLayout>
    )
}

RpqsIndicatorDialog.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    mainCode: PropTypes.string,
    indicators: PropTypes.arrayOf(rpqsIndicatorShape),
    rpqs: PropTypes.shape({ year: PropTypes.number, collectivityId: PropTypes.number }),
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    refValues: PropTypes.objectOf(PropTypes.number),
    onSaved: PropTypes.func,
}

export default RpqsIndicatorDialog